import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BeatLoader } from 'react-spinners';
import RestService from '../Services/RestService';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomCheckbox from '../Components/OsloKommuneUI/OKomCheckbox';
import OKomModal from '../Components/OsloKommuneUI/OKomModal';
import { useUserContext } from '../Components/StateContext/UserContext';

// Editable copy of one athletics participant. Gender uses language-neutral codes 'boy'/'girl'
// (shown as Gutt/Jente). DateOfBirth is free-text in Norwegian dd.MM.yyyy form.
interface EditableAthleticsRow {
  Id: number;
  Firstname: string;
  Lastname: string;
  FirstnameTamil: string;
  LastnameTamil: string;
  DateOfBirth: string;
  Gender: string;
  House: string;
  DepartmentId: number | null;   // editable for admins (dropdown); read-only name for localadmins
  EventIds: number[];   // read-only — shown as a comma-separated list of event names
}

// ISO "2015-06-01T00:00:00" → "01.06.2015" for the free-text input.
const isoToNo = (iso?: string): string => {
  if (!iso) return '';
  const p = iso.slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : '';
};

// Age (this year) from the free-text "dd.MM.yyyy" birth date, mirroring the registration
// form's ageFromDob. Returns null when no 4-digit year can be read, in which case the
// events modal can't apply the øvelsesmatrise and falls back to showing every event.
const ageFromNoDob = (dob?: string): number | null => {
  const parts = (dob ?? '').split('.').map(s => s.trim());
  const year = parts.length === 3 ? parseInt(parts[2], 10) : NaN;
  return Number.isFinite(year) && year > 1900 ? new Date().getFullYear() - year : null;
};

const toRow = (p: Api.AthleticsRegistrationDto): EditableAthleticsRow => ({
  Id: p.Id,
  Firstname: p.Firstname || '',
  Lastname: p.Lastname || '',
  FirstnameTamil: p.FirstnameTamil || '',
  LastnameTamil: p.LastnameTamil || '',
  DateOfBirth: isoToNo(p.DateOfBirth),
  Gender: p.Gender || '',
  House: p.House || '',
  DepartmentId: p.DepartmentId ?? null,
  EventIds: p.EventIds || [],
});

// One field "cell": small label above the input, fixed-ish width so rows wrap tidily.
const fieldStyle: React.CSSProperties = { flex: '1 1 9rem', minWidth: '8rem' };

const EditAthleticsParticipantsView: React.FC<RouteComponentProps> = () => {
  const { userRoles } = useUserContext();
  const isAdmin = userRoles.includes('admin');
  const canEdit = userRoles.includes('localadmin') || isAdmin;

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<EditableAthleticsRow[]>([]);
  // Snapshot of the last *saved* state — banner, house counts and the "missing" filter read from
  // this, so they only change after Lagre, not while the localadmin is still typing/selecting.
  const [saved, setSaved] = useState<EditableAthleticsRow[]>([]);
  const [events, setEvents] = useState<Api.AthleticsEventDto[]>([]);
  const [departments, setDepartments] = useState<Api.Department[]>([]);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);

  // Admin-only "modify events" modal — holds the participant id being edited and the
  // working set of selected event ids.
  const [eventsModalRowId, setEventsModalRowId] = useState<number | null>(null);
  const [modalEventIds, setModalEventIds] = useState<number[]>([]);
  const [savingEvents, setSavingEvents] = useState(false);
  // Admin-only delete confirmation — the row pending deletion (null = no prompt open).
  const [deleteRowId, setDeleteRowId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!canEdit) return;
    (async () => {
      try {
        setIsLoading(true);
        const data = await RestService.get<Api.AthleticsRegistrationDto[]>('/api/athleticsregistration/ParticipantsForEditing');
        const mapped = (data || []).map(toRow);
        setRows(mapped);
        setSaved(mapped);
      } catch (e) {
        toast.error(`Kunne ikke laste deltakere: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [canEdit]);

  // Event id → Norwegian name, for the read-only "Øvelser" column.
  useEffect(() => {
    RestService.get<Api.AthleticsEventDto[]>('/api/athleticsregistration/GetEvents')
      .then(e => setEvents(e || []))
      .catch(() => { /* event labels are best-effort */ });
  }, []);

  const eventNameMap = useMemo(() => new Map(events.map(e => [e.Id, e.NameNo])), [events]);
  const eventsLabel = (ids: number[]) =>
    (ids || []).map(id => eventNameMap.get(id)).filter((n): n is string => !!n).join(', ');

  // Department id → name, for the read-only "Avdeling" column.
  useEffect(() => {
    RestService.get<Api.Department[]>('/api/examregistration/GetDepartments')
      .then(d => setDepartments(d || []))
      .catch(() => { /* department labels are best-effort */ });
  }, []);

  const deptNameMap = useMemo(() => new Map(departments.map(d => [d.Id, d.Name])), [departments]);
  const deptLabel = (id: number | null) => (id != null ? deptNameMap.get(id) || '' : '');

  const updateField = (id: number, field: keyof EditableAthleticsRow, value: string) =>
    setRows(rs => rs.map(r => (r.Id === id ? { ...r, [field]: value } : r)));

  // Department is numeric (or null for "no department"); admins can re-assign it.
  const updateDepartment = (id: number, value: string) =>
    setRows(rs => rs.map(r => (r.Id === id ? { ...r, DepartmentId: value ? parseInt(value, 10) : null } : r)));

  const save = async (row: EditableAthleticsRow) => {
    if (!row.Firstname.trim() || !row.Lastname.trim()) {
      toast.error('Fornavn og etternavn er påkrevd.');
      return;
    }
    setSavingId(row.Id);
    try {
      await RestService.post<any, { Message: string }>('/api/athleticsregistration/UpdateParticipant', {
        Id: row.Id,
        Firstname: row.Firstname.trim(),
        Lastname: row.Lastname.trim(),
        FirstnameTamil: row.FirstnameTamil.trim() || null,
        LastnameTamil: row.LastnameTamil.trim() || null,
        DateOfBirth: row.DateOfBirth.trim() || null,
        Gender: row.Gender || null,
        House: row.House || null,
        // Only admins can re-scope; the backend ignores this for localadmins.
        DepartmentId: row.DepartmentId,
      });
      // Commit this row into the saved snapshot so the counts/filter update only now.
      setSaved(s => s.map(r => (r.Id === row.Id ? { ...row } : r)));
      toast.success('Lagret.');
    } catch (e) {
      toast.error(`Kunne ikke lagre: ${e}`);
    } finally {
      setSavingId(null);
    }
  };

  const modalRow = useMemo(
    () => (eventsModalRowId != null ? rows.find(r => r.Id === eventsModalRowId) ?? null : null),
    [rows, eventsModalRowId],
  );

  // Age this year drives which events the modal offers — same source (e.Ages, the
  // øvelsesmatrise) the registration form uses. Computed from the saved DOB so the list
  // stays stable while editing.
  const modalAge = useMemo(() => (modalRow ? ageFromNoDob(modalRow.DateOfBirth) : null), [modalRow]);

  // Events to show as checkboxes: those eligible for the participant's age per the matrix,
  // plus any already-assigned event (so a re-save can never silently drop a selection that
  // the matrix no longer lists — e.g. legacy data or an unreadable birth date). When the age
  // is unknown we can't scope by the matrix, so every event is offered.
  const modalEventChoices = useMemo(() => {
    if (!modalRow) return [] as Api.AthleticsEventDto[];
    if (modalAge == null) return events;
    const current = new Set(modalRow.EventIds);
    return events.filter(e => e.Ages.includes(modalAge) || current.has(e.Id));
  }, [modalRow, modalAge, events]);

  const openEventsModal = (row: EditableAthleticsRow) => {
    setEventsModalRowId(row.Id);
    setModalEventIds([...row.EventIds]);
  };

  const closeEventsModal = () => {
    if (savingEvents) return;   // don't yank the modal out mid-request
    setEventsModalRowId(null);
  };

  const toggleModalEvent = (eventId: number, checked: boolean) =>
    setModalEventIds(ids => (checked ? Array.from(new Set([...ids, eventId])) : ids.filter(i => i !== eventId)));

  const saveEvents = async () => {
    if (eventsModalRowId == null) return;
    setSavingEvents(true);
    try {
      await RestService.post<{ Id: number; EventIds: number[] }, { Message: string }>(
        '/api/athleticsregistration/UpdateParticipantEvents',
        { Id: eventsModalRowId, EventIds: modalEventIds },
      );
      const next = [...modalEventIds];
      setRows(rs => rs.map(r => (r.Id === eventsModalRowId ? { ...r, EventIds: next } : r)));
      setSaved(s => s.map(r => (r.Id === eventsModalRowId ? { ...r, EventIds: next } : r)));
      toast.success('Øvelser oppdatert.');
      setEventsModalRowId(null);
    } catch (e) {
      toast.error(`Kunne ikke lagre øvelser: ${e}`);
    } finally {
      setSavingEvents(false);
    }
  };

  // The row queued for deletion (drives the confirm prompt's name/label).
  const deleteRow = useMemo(
    () => (deleteRowId != null ? rows.find(r => r.Id === deleteRowId) ?? null : null),
    [rows, deleteRowId],
  );

  const cancelDelete = () => {
    if (deleting) return;
    setDeleteRowId(null);
  };

  const deleteParticipant = async () => {
    if (deleteRowId == null) return;
    const id = deleteRowId;
    setDeleting(true);
    try {
      await RestService.del<{ Message: string }>(`/api/athleticsregistration/DeleteParticipant/${id}`);
      setRows(rs => rs.filter(r => r.Id !== id));
      setSaved(s => s.filter(r => r.Id !== id));
      // If the events modal happened to be open for this row, drop it too.
      setEventsModalRowId(curr => (curr === id ? null : curr));
      toast.success('Deltaker slettet.');
      setDeleteRowId(null);
    } catch (e) {
      toast.error(`Kunne ikke slette deltaker: ${e}`);
    } finally {
      setDeleting(false);
    }
  };

  // Saved-state lookup, so the "missing" filter reflects what's persisted (not unsaved edits).
  const savedById = useMemo(() => new Map(saved.map(r => [r.Id, r])), [saved]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      // "Vis kun mangler" → only rows whose SAVED gender and/or house is missing.
      const sv = savedById.get(r.Id);
      if (onlyMissing && sv && sv.Gender && sv.House) return false;
      if (q && !`${r.Firstname} ${r.Lastname} ${r.FirstnameTamil} ${r.LastnameTamil}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, savedById, query, onlyMissing]);

  // Banner + house tallies read from the saved snapshot, so they only update after Lagre.
  const missingHouse = useMemo(() => saved.filter(r => !r.House).length, [saved]);
  const missingGender = useMemo(() => saved.filter(r => !r.Gender).length, [saved]);
  const gulCount = useMemo(() => saved.filter(r => r.House === 'gul').length, [saved]);
  const rodCount = useMemo(() => saved.filter(r => r.House === 'rød').length, [saved]);

  // Only mention the fields that actually have missing values; hide the banner entirely if none.
  const bannerParts: string[] = [];
  if (missingHouse > 0) bannerParts.push(`hus på ${missingHouse} deltakere`);
  if (missingGender > 0) bannerParts.push(`kjønn på ${missingGender} deltakere`);
  const bannerText = bannerParts.length ? 'Mangler ' + bannerParts.join(', mangler ') : '';

  if (!canEdit) {
    return (
      <>
        <OKomBreadcrumb path='Påmelding, Endre påmeldinger athletics' />
        <p className="ods-text--align-center ods-margin-top-5">Du har ikke tilgang til denne siden.</p>
      </>
    );
  }

  return (
    <>
      <OKomBreadcrumb path='Påmelding, Endre påmeldinger athletics' />
      {isLoading && <OKomLoading text='Henter deltakere …' />}

      <div className="ods-margin-top-5" style={{ padding: '0 1.5rem' }}>
        {bannerText && (
          <div style={{ background: '#a4133c', color: '#fff', padding: '0.75rem 1rem', borderRadius: 4, fontWeight: 600 }}>
            {bannerText}
          </div>
        )}

        <div className="ods-margin-top-2" style={{ fontWeight: 600 }}>
          Gul: {gulCount} deltakere, Rød: {rodCount} deltakere
        </div>

        <div className="ods-search-inline ods-margin-top-3" style={{ maxWidth: '28rem' }}>
          <label className="ods-search-inline__label">
            <input
              placeholder='Søk på navn'
              className="ods-search-inline__input"
              type="search"
              autoComplete="off"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </label>
        </div>

        {bannerText && (
          <div className="ods-margin-top-2">
            <OKomCheckbox checked={onlyMissing} onChange={setOnlyMissing}>Vis kun mangler</OKomCheckbox>
          </div>
        )}

        <div className="ods-margin-top-3">
          {filtered.map(row => (
            <div
              key={row.Id}
              className="okom-edit-athletics-row"
              style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}
            >
              <label className="ods-input__label" style={fieldStyle}>Fornavn
                <input className="ods-input__input" value={row.Firstname} onChange={e => updateField(row.Id, 'Firstname', e.target.value)} />
              </label>
              <label className="ods-input__label" style={fieldStyle}>Etternavn
                <input className="ods-input__input" value={row.Lastname} onChange={e => updateField(row.Id, 'Lastname', e.target.value)} />
              </label>
              <label className="ods-input__label" style={fieldStyle}>Fornavn (tamil)
                <input className="ods-input__input" value={row.FirstnameTamil} onChange={e => updateField(row.Id, 'FirstnameTamil', e.target.value)} />
              </label>
              <label className="ods-input__label" style={fieldStyle}>Etternavn (tamil)
                <input className="ods-input__input" value={row.LastnameTamil} onChange={e => updateField(row.Id, 'LastnameTamil', e.target.value)} />
              </label>
              <label className="ods-input__label" style={fieldStyle}>Fødselsdato
                <input className="ods-input__input" placeholder="dd.mm.åååå" value={row.DateOfBirth} onChange={e => updateField(row.Id, 'DateOfBirth', e.target.value)} />
              </label>
              <label className="ods-input__label" style={{ flex: '0 1 8rem', minWidth: '7rem' }}>Kjønn
                <div className="ods-select__select">
                  <select value={row.Gender} onChange={e => updateField(row.Id, 'Gender', e.target.value)}>
                    <option value="">–</option>
                    <option value="boy">Gutt</option>
                    <option value="girl">Jente</option>
                  </select>
                </div>
              </label>
              <label className="ods-input__label" style={{ flex: '0 1 8rem', minWidth: '7rem' }}>Lag (hus)
                <div className="ods-select__select">
                  <select value={row.House} onChange={e => updateField(row.Id, 'House', e.target.value)}>
                    <option value="">–</option>
                    <option value="rød">Rød</option>
                    <option value="gul">Gul</option>
                  </select>
                </div>
              </label>
              <label className="ods-input__label" style={{ flex: '1 1 10rem', minWidth: '9rem' }}>Avdeling
                {isAdmin ? (
                  <div className="ods-select__select">
                    <select value={row.DepartmentId ?? ''} onChange={e => updateDepartment(row.Id, e.target.value)}>
                      <option value="">–</option>
                      {departments.map(d => (<option key={d.Id} value={d.Id}>{d.Name}</option>))}
                    </select>
                  </div>
                ) : (
                  <input className="ods-input__input" value={deptLabel(row.DepartmentId)} disabled readOnly style={{ backgroundColor: '#dadada' }} />
                )}
              </label>
              <label className="ods-input__label" style={{ flex: '1 1 12rem', minWidth: '10rem' }}>Øvelser
                {isAdmin ? (
                  <button
                    type="button"
                    className="ods-input__input"
                    onClick={() => openEventsModal(row)}
                    title="Klikk for å endre øvelser"
                    style={{ textAlign: 'left', cursor: 'pointer', background: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {eventsLabel(row.EventIds) || 'Velg øvelser …'}
                  </button>
                ) : (
                  <input className="ods-input__input" value={eventsLabel(row.EventIds)} disabled readOnly style={{ backgroundColor: '#dadada' }} />
                )}
              </label>
              <button type="button" className="ods-button" style={{ flex: '0 0 auto' }} onClick={() => save(row)} disabled={savingId === row.Id}>
                Lagre{savingId === row.Id && <span className="ods-margin-left-1" style={{ verticalAlign: 'middle' }}><BeatLoader color="#ffffff" size={6} /></span>}
              </button>
              {isAdmin && (
                <button type="button" className="ods-button ods-button--red" style={{ flex: '0 0 auto' }} onClick={() => setDeleteRowId(row.Id)}>
                  Slett
                </button>
              )}
            </div>
          ))}
          {filtered.length === 0 && !isLoading && (
            <p className="ods-text--align-center ods-color-text-grayscale-10" style={{ padding: '1rem' }}>
              {rows.length === 0 ? 'Ingen deltakere i din avdeling.' : 'Ingen treff.'}
            </p>
          )}
        </div>
      </div>

      {isAdmin && (
        <OKomModal
          open={eventsModalRowId != null}
          onClose={closeEventsModal}
          labelledBy="edit-events-title"
          popupStyle={{ maxWidth: '44rem' }}
        >
          {modalRow && (
            <>
              <h2 id="edit-events-title" style={{ marginTop: 0 }}>
                Endre øvelser – {modalRow.Firstname} {modalRow.Lastname}
              </h2>
              <p className="ods-text--size-mike ods-color-text-grayscale-10" style={{ marginTop: 0 }}>
                {modalAge != null
                  ? `Viser øvelser fra øvelsesmatrisen for ${modalAge} år.`
                  : 'Fødselsår mangler eller er ugyldig – viser alle øvelser.'}
              </p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(12rem, 1fr))',
                  gap: '0.5rem 1.25rem',
                  maxHeight: '50vh',
                  overflowY: 'auto',
                }}
              >
                {modalEventChoices.map(ev => (
                  <OKomCheckbox
                    key={ev.Id}
                    checked={modalEventIds.includes(ev.Id)}
                    onChange={c => toggleModalEvent(ev.Id, c)}
                  >
                    {ev.NameNo}
                  </OKomCheckbox>
                ))}
              </div>
              {modalEventChoices.length === 0 && (
                <p className="ods-color-text-grayscale-10">Ingen øvelser tilgjengelig for denne alderen.</p>
              )}

              <div
                className="ods-margin-top-4"
                style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}
              >
                <button type="button" className="ods-button" onClick={saveEvents} disabled={savingEvents}>
                  Lagre øvelser
                  {savingEvents && <span className="ods-margin-left-1" style={{ verticalAlign: 'middle' }}><BeatLoader color="#ffffff" size={6} /></span>}
                </button>
              </div>
            </>
          )}
        </OKomModal>
      )}

      {isAdmin && (
        <OKomModal
          open={deleteRowId != null}
          onClose={cancelDelete}
          labelledBy="delete-participant-title"
          hideCloseButton
          popupStyle={{ maxWidth: '32rem' }}
        >
          <h2 id="delete-participant-title" style={{ marginTop: 0 }}>Slett deltaker</h2>
          <p>
            Vil du virkelig slette{' '}
            <strong>{deleteRow ? `${deleteRow.Firstname} ${deleteRow.Lastname}`.trim() : 'deltakeren'}</strong>? Handlingen kan ikke angres.
          </p>
          <div className="ods-margin-top-4" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button type="button" className="ods-button ods-button--outline" onClick={cancelDelete} disabled={deleting}>
              Avbryt
            </button>
            <button type="button" className="ods-button ods-button--red" onClick={deleteParticipant} disabled={deleting}>
              Slett
              {deleting && <span className="ods-margin-left-1" style={{ verticalAlign: 'middle' }}><BeatLoader color="#ffffff" size={6} /></span>}
            </button>
          </div>
        </OKomModal>
      )}
    </>
  );
};

export default withRouter(EditAthleticsParticipantsView);
